import { useEffect, useState } from "react";
import { api, StockTakeSession, InventoryRow } from "../api";
import { formatDate } from "../utils";
import { searchItems } from "../search";
import { GlassPanel, KpiCard, Badge, Modal, toast, confirm, SkeletonTable, SkeletonKpi, ErrorState } from "../components";
import ItemAuditHistory from "../components/ItemAuditHistory";

type TabKey = "count" | "history";
const TAB_STORAGE_KEY = "stock_take_tab";

interface CountItem {
  item_id: string;
  variant_id: string;
  item_name: string;
  sku: string;
  barcode: string;
  expected_qty: number;
  actual_qty: number | null;
}

/** Trang Kiểm kê tồn kho (P2-4) — FULL */
export default function StockTakePage() {
  const [sessions, setSessions] = useState<StockTakeSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [newNotes, setNewNotes] = useState("");
  const [creating, setCreating] = useState(false);

  // Detail view state
  const [activeSession, setActiveSession] = useState<StockTakeSession | null>(null);
  const [items, setItems] = useState<CountItem[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchSku, setSearchSku] = useState("");
  const [countInputs, setCountInputs] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [tab, setTab] = useState<TabKey>(
    () => (localStorage.getItem(TAB_STORAGE_KEY) as TabKey) || "count"
  );
  useEffect(() => { localStorage.setItem(TAB_STORAGE_KEY, tab); }, [tab]);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const r = await api.stockTake.sessions();
      setSessions(r.sessions || []);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.stockTake.create({ session_date: newDate, notes: newNotes || undefined });
      toast.success("Đã tạo session kiểm kê");
      setShowCreate(false);
      setNewDate(new Date().toISOString().slice(0, 10));
      setNewNotes("");
      await loadSessions();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi tạo session");
    } finally {
      setCreating(false);
    }
  };

  const openSession = async (session: StockTakeSession) => {
    setActiveSession(session);
    setDetailLoading(true);
    setSearchSku("");
    setCountInputs({});
    try {
      // Fetch inventory items as base for counting
      const inv = await api.inventory({ limit: 500 });
      const stockItems: CountItem[] = (inv.inventory || []).map((row: InventoryRow) => ({
        item_id: row.item_id,
        variant_id: row.variant_id,
        item_name: row.item_name,
        sku: row.sku || "",
        barcode: row.barcode || "",
        expected_qty: row.in_stock,
        actual_qty: null,
      }));
      setItems(stockItems);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCount = (itemId: string, variantId: string, value: string) => {
    const key = `${itemId}_${variantId}`;
    setCountInputs((prev) => ({ ...prev, [key]: value }));
    setItems((prev) =>
      prev.map((item) =>
        item.item_id === itemId && item.variant_id === variantId
          ? { ...item, actual_qty: value === "" ? null : Number(value) }
          : item
      )
    );
  };

  const handleSave = async () => {
    if (!activeSession) return;
    const counted = items.filter((i) => i.actual_qty !== null);
    if (counted.length === 0) { toast.warning("Chưa nhập số lượng kiểm kê"); return; }
    setSaving(true);
    try {
      await api.stockTake.updateItems(
        activeSession.id,
        counted.map((i) => ({
          item_id: i.item_id,
          variant_id: i.variant_id,
          actual_qty: i.actual_qty!,
        }))
      );
      toast.success(`Đã lưu ${counted.length} mục kiểm kê`);
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi lưu");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!activeSession) return;
    if (!await confirm({ title: "Hoàn tất kiểm kê", message: "Tồn kho sẽ được điều chỉnh theo số thực tế.", danger: true, confirmLabel: "Hoàn tất" })) return;
    setSaving(true);
    try {
      const r = await api.stockTake.complete(activeSession.id);
      toast.success(`Hoàn tất! Đã điều chỉnh ${r.adjusted} mục.`);
      setActiveSession(null);
      await loadSessions();
    } catch (e: unknown) {
      toast.error((e as Error).message || "Lỗi hoàn tất");
    } finally {
      setSaving(false);
    }
  };

  const filtered = searchSku
    ? searchItems(items, searchSku, (i) => ({
        id: i.item_id,
        barcode: i.barcode,
        sku: i.sku,
        name: i.item_name,
      })).map(({ item }) => item)
    : items;

  const countedCount = items.filter((i) => i.actual_qty !== null).length;
  const diffCount = items.filter((i) => i.actual_qty !== null && i.actual_qty !== i.expected_qty).length;

  // Detail view
  if (activeSession) {
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <button className="btn btn-ghost" onClick={() => setActiveSession(null)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span>
            Quay lại
          </button>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>
            Kiểm kê {formatDate(activeSession.session_date)}
          </h3>
          <Badge variant={activeSession.status === "completed" ? "ok" : "info"}>
            {activeSession.status === "completed" ? "Hoàn tất" : "Đang mở"}
          </Badge>
        </div>

        <div className="kpi-grid">
          <KpiCard label="Tổng SP" value={items.length.toString()} dotColor="blue" note="trong kho" />
          <KpiCard label="Đã kiểm" value={countedCount.toString()} dotColor="ok" note={`/ ${items.length}`} />
          <KpiCard label="Chênh lệch" value={diffCount.toString()} dotColor={diffCount > 0 ? "bad" : "ok"} note="thiếu/thừa" />
        </div>

        <GlassPanel title="Kiểm kê tồn kho" badge={`${filtered.length} SP`}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div className="search" style={{ flex: 1, maxWidth: 280 }}>
              <span className="material-symbols-outlined">search</span>
              <input
                placeholder="Tìm tên / SKU / barcode…"
                value={searchSku}
                onChange={(e) => setSearchSku(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }} />
            {activeSession.status !== "completed" && (
              <>
                <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
                  Lưu ({countedCount})
                </button>
                <button className="btn" onClick={handleComplete} disabled={saving}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check_circle</span>
                  Hoàn tất session
                </button>
              </>
            )}
          </div>

          {detailLoading ? (
            <div className="loading">Đang tải…</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Sản phẩm</th>
                    <th>SKU</th>
                    <th style={{ textAlign: "right" }}>Tồn hệ thống</th>
                    <th style={{ textAlign: "right" }}>Thực tế</th>
                    <th style={{ textAlign: "right" }}>Chênh lệch</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => {
                    const key = `${item.item_id}_${item.variant_id}`;
                    const variance = item.actual_qty !== null ? item.actual_qty - item.expected_qty : 0;
                    return (
                      <tr key={key}>
                        <td style={{ fontWeight: 600 }}>{item.item_name}</td>
                        <td style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{item.sku || "—"}</td>
                        <td style={{ textAlign: "right" }}>{item.expected_qty}</td>
                        <td style={{ textAlign: "right" }}>
                          {activeSession.status === "completed" ? (
                            item.actual_qty ?? "—"
                          ) : (
                            <input
                              type="number"
                              style={{ width: 80, textAlign: "right", fontSize: 13 }}
                              placeholder="0"
                              value={countInputs[key] ?? ""}
                              onChange={(e) => handleCount(item.item_id, item.variant_id, e.target.value)}
                            />
                          )}
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontWeight: 700,
                            color: variance > 0 ? "var(--accent-green)" : variance < 0 ? "var(--accent-red)" : undefined,
                          }}
                        >
                          {item.actual_qty !== null ? (variance > 0 ? `+${variance}` : variance) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </GlassPanel>
      </>
    );
  }

  // Sessions list
  return (
    <>
      <div className="tabs" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`tab-item ${tab === "count" ? "active" : ""}`}
          onClick={() => setTab("count")}
        >
          Kiểm kê
        </button>
        <button
          type="button"
          className={`tab-item ${tab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          Lịch sử thay đổi
        </button>
      </div>

      {tab === "history" ? (
        <ItemAuditHistory />
      ) : (
      <GlassPanel title="Kiểm kê tồn kho" badge={`${sessions.length} session`}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button className="btn" onClick={() => setShowCreate(true)}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            Tạo session mới
          </button>
        </div>

        {loading ? (
          <>
            <div className="kpi-grid">
              <SkeletonKpi />
              <SkeletonKpi />
              <SkeletonKpi />
            </div>
            <SkeletonTable rows={5} cols={5} />
          </>
        ) : error ? (
          <ErrorState error={error} onRetry={loadSessions} />
        ) : sessions.length === 0 ? (
          <div className="empty">Chưa có session kiểm kê nào</div>
        ) : (
          <div className="list">
            {sessions.map((s) => (
              <div
                className="list-item"
                key={s.id}
                style={{ cursor: "pointer" }}
                onClick={() => openSession(s)}
              >
                <div>
                  <div className="name">
                    Kiểm kê {formatDate(s.session_date)}
                    {s.notes && <span style={{ fontWeight: 400, color: "var(--text-tertiary)", marginLeft: 8 }}>— {s.notes}</span>}
                  </div>
                  <div className="meta">
                    Tạo: {formatDate(s.created_at)}
                    {s.completed_at && ` · Xong: ${formatDate(s.completed_at)}`}
                    {s.total_items != null && ` · ${s.total_items} SP`}
                  </div>
                </div>
                <div className="right">
                  <Badge variant={s.status === "completed" ? "ok" : "info"}>
                    {s.status === "completed" ? "Hoàn tất" : "Đang mở"}
                  </Badge>
                  {s.diff_count != null && s.diff_count > 0 && (
                    <div style={{ fontSize: 11, color: "var(--accent-red)", marginTop: 2 }}>
                      {s.diff_count} lệch
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>
      )}

      {/* Modal tạo session */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Tạo session kiểm kê"
        footer={
          <button className="btn" onClick={handleCreate} disabled={creating}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            {creating ? "Đang tạo…" : "Bắt đầu kiểm kê"}
          </button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ngày kiểm kê</label>
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
          />
          <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Ghi chú</label>
          <input
            placeholder="VD: Kiểm kê tháng 5/2026"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
